import { Box, ButtonBase, Popover } from '@mui/material';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import { ReactElement, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

const c = tokens.color;

/** A POOL bonus condition, as the documents carry it. */
export interface PaloBonusCondition {
    id: number;
    title: string;
    description?: string | null;
    mode: 'percentage' | 'amount' | string;
    percentage?: number | null;
    /** For a bonus by amount: what one unit brings. */
    price?: number | null;
    unit?: string | null;
    conditionFrom?: string | Date | null;
    conditionTo?: string | Date | null;
}

/** Whether a condition holds on a day; open ends hold for ever. */
export const paloBonusConditionHolds = (condition: PaloBonusCondition, at: Date = new Date()): boolean => {
    const day = at.getTime();
    const from = condition.conditionFrom ? new Date(condition.conditionFrom).getTime() : -Infinity;
    const to = condition.conditionTo ? new Date(condition.conditionTo).getTime() : Infinity;

    return from <= day && day <= to;
};

export interface PaloBonusBadgeProps {
    conditions?: PaloBonusCondition[] | null;
    /** The document's date; only the conditions that hold then count. */
    at?: Date | string | null;
    /** Who the conditions are of, for the heading of the window. */
    supplierName?: string;
}

/**
 * The POOL bonus a supplier grants, the same wherever a supplier stands in a
 * document.
 *
 * The gift alone said that there was something, not what: the rate stood only
 * in a tooltip, and not even there. It now stands beside the gift — the
 * highest rate and how many more conditions there are — and a click shows
 * them all (GF 14.10.2026, #2256).
 */
export const PaloBonusBadge = ({ conditions, at, supplierName }: PaloBonusBadgeProps): ReactElement => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const day = at ? new Date(at) : new Date();
    const holding = (conditions ?? []).filter(condition => paloBonusConditionHolds(condition, day));
    const rates = holding
        .filter(condition => 'percentage' === condition.mode && null != condition.percentage)
        .map(condition => condition.percentage as number);
    const top = rates.length > 0 ? Math.max(...rates) : undefined;

    if (0 === holding.length) {
        // A grey gift read as a bonus; nothing to grant is a dash (R-182).
        return (
            <Box component="span" aria-label="Kein POOL-Bonus" sx={{ color: c.ink3 }}>
                –
            </Box>
        );
    }

    return (
        <>
            <ButtonBase
                onClick={event => {
                    event.stopPropagation();
                    setAnchor(event.currentTarget);
                }}
                sx={{
                    gap: '4px',
                    padding: '1px 6px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    color: c.kindBonus,
                    backgroundColor: c.kindBonusSoft,
                    '&:hover': { filter: 'brightness(.96)' },
                }}
            >
                <span>🎁</span>
                {undefined !== top ? (
                    <span>
                        <FormattedNumber value={top} maximumFractionDigits={2} /> %
                    </span>
                ) : (
                    <FormattedMessage id="app.bonus.badge.noRate" defaultMessage="Bonus" />
                )}
                {holding.length > 1 && (
                    <Box component="span" sx={{ color: c.ink3 }}>
                        +{holding.length - 1}
                    </Box>
                )}
            </ButtonBase>
            <Popover
                open={!!anchor}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                onClick={event => event.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{ paper: { sx: { padding: '10px 12px', maxWidth: 420 } } }}
            >
                <Box sx={{ fontSize: '12.5px', fontWeight: 620, mb: '8px' }}>
                    <FormattedMessage id="app.bonus.badge.title" defaultMessage="POOL-Bonus" />
                    {supplierName && (
                        <Box component="span" sx={{ fontWeight: 450, color: c.ink3 }}>
                            {' · '}
                            {supplierName}
                        </Box>
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {holding.map(condition => (
                        <Box key={condition.id} sx={{ fontSize: '12px', lineHeight: 1.45 }}>
                            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'baseline' }}>
                                <Box sx={{ fontWeight: 600 }}>{condition.title}</Box>
                                <Box sx={{ marginLeft: 'auto', whiteSpace: 'nowrap', color: c.kindBonus }}>
                                    {'percentage' === condition.mode && null != condition.percentage ? (
                                        <>
                                            <FormattedNumber value={condition.percentage} maximumFractionDigits={2} /> %
                                        </>
                                    ) : null != condition.price ? (
                                        <FormattedMessage
                                            id="app.bonus.badge.perUnit"
                                            defaultMessage="{amount} je Einheit"
                                            values={{
                                                amount: (
                                                    <FormattedNumber
                                                        value={condition.price}
                                                        minimumFractionDigits={2}
                                                        maximumFractionDigits={2}
                                                    />
                                                ),
                                            }}
                                        />
                                    ) : null}
                                </Box>
                            </Box>
                            {(condition.conditionFrom || condition.conditionTo) && (
                                <Box sx={{ color: c.ink3, fontSize: '11px' }}>
                                    <FormattedMessage id="app.bonus.badge.valid" defaultMessage="gültig" />{' '}
                                    {condition.conditionFrom ? <FormattedDate value={condition.conditionFrom} /> : '…'}
                                    {' – '}
                                    {condition.conditionTo ? <FormattedDate value={condition.conditionTo} /> : '…'}
                                </Box>
                            )}
                            {condition.description && <Box sx={{ color: c.ink2 }}>{condition.description}</Box>}
                        </Box>
                    ))}
                </Box>
            </Popover>
        </>
    );
};
